/* global checkStatusUrl integrationConfigured deleteTransactionUrl refreshTransactionUrl */

import React, { Fragment, memo, useEffect, useState } from 'react';

import { isObject } from 'formik';
import { isArray, isNull } from 'lodash';
import DatePicker from 'react-datepicker';

import ActualCostsAllocationSubRow, { TRUNCATE_CELL_CLASS } from './ActualCostsAllocationSubRow';
import { SEND_FOR_COMMITMENT_OPTION, isPlaceholderAllocation } from './allocationHelpers';

import { formatDateWithTz } from 'Helpers/dateUtils';
import { elideMiddle, formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';
import { generateKey } from 'Helpers/misc';

import ContentListItem from 'CommonComponents/ContentListItem';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import { useToast } from 'Hooks/useToast';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CircleCancelIcon from 'Assets/icons/circle-xmark.svg';
import PlusCircleIcon from 'Assets/icons/plus.svg';

import 'react-datepicker/dist/react-datepicker.css';

/**
 * Document Reference is a long opaque identifier in a column narrow enough that
 * end-truncation would hide the part that distinguishes one reference from the
 * next, so it is middle-elided and the full value lives on hover. Copying is the
 * only thing anyone does with it, so a click copies — which has to be kept to
 * itself, since the surrounding cells open the row editor on click.
 */
function DocumentReferenceCell({ value }) {
    const toast = useToast();

    if (!value) {
        return null;
    }

    const copy = (event) => {
        event.stopPropagation();

        if (!navigator.clipboard?.writeText) {
            toast.error($t('Copying is not available in this browser.'));
            return;
        }

        navigator.clipboard.writeText(value).then(
            () => toast.success($t('Document reference copied.')),
            () => toast.error($t('The document reference could not be copied.')),
        );
    };

    return (
        <Tooltip
            placement="right-end"
            tooltipType="text"
            // Wrapped in an element on purpose: TextTooltip pushes STRING children
            // through dangerouslySetInnerHTML, and a document reference arrives from
            // an Excel import or an ERP sync. As an element it goes down the escaped
            // branch instead.
            tooltipContent={<span>{$t('{{reference}} — click to copy', { reference: value })}</span>}
        >
            {/* A real button, not a span with role="button": the copy action has to be
                reachable by keyboard, and an ARIA role alone does not bring Enter /
                Space activation with it. */}
            <button type="button" className="tw-cursor-pointer tw-whitespace-nowrap tw-bg-transparent tw-p-0 tw-text-start" onClick={copy}>
                {elideMiddle(value)}
            </button>
        </Tooltip>
    );
}

const ActualCostsAllocationRow = memo(function ActualCostsAllocationRow(props) {
    const [costAllocations, setCostAllocations] = useState([]);
    const [type, setType] = useState(props.actualCosts.type);
    const [saveUrl, setSaveUrl] = useState('');
    const [tax, setTax] = useState({});
    const [taxCanBeEdited, setTaxCanBeEdited] = useState(false);
    const [suggestions, setSuggestions] = useState({
        workforce: {},
        non_workforce: {},
    });

    const [allocationsChanged, setAllocationsChanged] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [defaultGeneralDate, setDefaultGeneralDate] = useState({});
    const [checkTransactionStatusUrl, setCheckTransactionStatusUrl] = useState('');
    const [deleteUrl, setDeleteUrl] = useState('');
    const [refreshUrl, setRefreshUrl] = useState('');
    const [canBeDeleted, setCanBeDeleted] = useState(false);

    const truncateInReadMode = editMode ? '' : TRUNCATE_CELL_CLASS;

    useEffect(() => {
        if (checkStatusUrl !== undefined && !!integrationConfigured) {
            const url = checkStatusUrl.replace('ACTUAL_COST_ID', props.actualCosts.id);
            setCheckTransactionStatusUrl(url);
        }
        if (deleteTransactionUrl !== undefined && !!integrationConfigured) {
            const delUrl = deleteTransactionUrl.replace('ACTUAL_COST_ID', props.actualCosts.id);
            setDeleteUrl(delUrl);
        }

        if (refreshTransactionUrl !== undefined && !!integrationConfigured) {
            const rUrl = refreshTransactionUrl.replace('ACTUAL_COST_ID', props.actualCosts.id);
            setRefreshUrl(rUrl);
        }
    }, []);

    useEffect(() => {
        setTaxObject();

        const tips = JSON.parse(JSON.stringify(suggestions));
        if (isObject(props.actualCosts.suggestions.non_workforce)) {
            tips.non_workforce = props.actualCosts.suggestions.non_workforce;
        }
        if (isObject(props.actualCosts.suggestions.workforce)) {
            tips.workforce = props.actualCosts.suggestions.workforce;
        }

        setSuggestions(tips);
        setDefaultAllocation();
        refreshDefaultDate();
    }, []);
    useEffect(() => {
        setTaxObject();
    }, [props.actualCosts.tax_id]);

    const setTaxObject = () => {
        if (props.taxEnabled) {
            let taxId = props.actualCosts.tax_id;
            setTaxCanBeEdited(isNull(props.actualCosts.tax_excluded));
            const taxItem = props.taxes.find((item) => {
                return taxId == item.id;
            });
            setTax(taxItem);
        }
    };

    useEffect(() => {
        if (!allocationsChanged) {
            setDefaultAllocation();

            return;
        }

        // `allocationsChanged` normally means "the reconciler has unsaved work
        // here", which fresh server data must not overwrite. But a row that
        // arrived with nothing allocated sets the flag on mount just by
        // synthesising its placeholder allocation (see setDefaultAllocation), so
        // it would sit on that placeholder for the rest of its life — including
        // after a bulk apply writes real allocations to it and the table re-reads
        // both lists. That is the whole unreconciled list on a freshly imported
        // event, and the cell reads as empty because the placeholder holds
        // whatever the ERP suggested, which for those rows is usually nothing.
        //
        // Holding only placeholders means nothing here was persisted — it does
        // NOT mean nothing was typed. The reconciler may have set OTHER columns
        // on this exact row (Type, Transaction, Budget Line) that no save has
        // ever carried to the server, and a bulk apply only tells the server
        // about the one column it targeted. `props.cachedAllocations` is the
        // table's own copy of this row's in-memory allocations — already patched
        // with the bulk-applied column by `reconcileInMemoryEdits` in
        // ActualCostsAllocationTable — so it is preferred over the raw server
        // response for every field except `id`. The id is taken from the server
        // regardless, because adopting it is what turns `isPlaceholderAllocation`
        // false: without a real id, a later delete on this allocation would skip
        // the server request entirely and leave it orphaned there.
        const holdsOnlyPlaceholders = costAllocations.length > 0 && costAllocations.every((allocation) => isPlaceholderAllocation(allocation));

        if (props.actualCosts.costAllocations.length > 0 && holdsOnlyPlaceholders) {
            const cached = props.cachedAllocations ?? [];
            const adopted = props.actualCosts.costAllocations.map((serverAllocation, index) => {
                const cachedAllocation = cached[index];

                return cachedAllocation ? { ...cachedAllocation, id: serverAllocation.id } : serverAllocation;
            });

            setCostAllocations(JSON.parse(JSON.stringify(adopted)));
        }
    }, [props.actualCosts.costAllocations]);

    useEffect(() => {
        if (!props.onAllocationsChange || !allocationsChanged) {
            return;
        }
        props.onAllocationsChange(props.actualCosts.id, costAllocations);
    }, [costAllocations]);

    const refreshTransaction = () => {
        if (refreshUrl) {
            axios
                .put(refreshUrl, {})
                .then((res) => {
                    props.toast.success($t('Transaction refreshed successfully.'));
                    props.fetchData();
                })
                .catch((err) => {
                    console.log(err.response?.data);
                    props.toast.error($t('Something went wrong while deleting the transaction'));
                });
        }
    };

    const checkActualCostsStatus = () => {
        if (checkTransactionStatusUrl) {
            axios
                .get(checkTransactionStatusUrl)
                .then((res) => {
                    const { status, statusName } = res.data;
                    if (status) {
                        props.toast.success($t('Transaction status: {{name}}', { name: statusName }));
                    } else {
                        if (
                            confirm(
                                $t('Transaction status: {{name}}. It is recommended to delete this transaction in the app. Delete transaction?', {
                                    name: statusName,
                                }),
                            )
                        ) {
                            deleteTransaction(true);
                        }
                    }
                })
                .catch((err) => {
                    console.log(err.response.data);
                });
        }
    };

    const deleteTransaction = (remove = false) => {
        if (remove && deleteUrl) {
            axios
                .delete(deleteUrl)
                .then((res) => {
                    props.toast.success($t('Transaction deleted successfully.'));
                    props.fetchData();
                })
                .catch((err) => {
                    props.toast.error($t('Something went wrong while deleting the transaction'));
                });
        }
    };
    const refreshDefaultDate = () => {
        const defaultDate = {
            transaction_description: props.actualCosts.transaction_description,
            amount: props.actualCosts.amount,
            document_type: props.actualCosts.document_type,
            document_number: props.actualCosts.document_number,
            date: props.actualCosts.date,
            gl_code: props.actualCosts.gl_code,
            supplier_name: props.actualCosts.supplier_name,
            supplier: props.actualCosts.supplier,
        };

        setDefaultGeneralDate(defaultDate);
    };

    const setDefaultAllocation = () => {
        if (props.cachedAllocations) {
            setCostAllocations(JSON.parse(JSON.stringify(props.cachedAllocations)));
            setAllocationsChanged(true);
            setSaveUrl(props.actualCosts.syncAllocationsUrl);
            return;
        }

        const existingAllocations = JSON.parse(JSON.stringify(props.actualCosts.costAllocations));
        const addDefault = existingAllocations.length === 0;

        if (addDefault) {
            const item = defaultAllocationData(existingAllocations);
            existingAllocations.push(item);
            setAllocationsChanged(true);
        }

        setCostAllocations(existingAllocations);
        const saveAllocationUrl = props.actualCosts.syncAllocationsUrl;
        setSaveUrl(saveAllocationUrl);
    };

    const calculateRowSpanFromRow = () => {
        return costAllocations.length > 0 ? costAllocations.length : 1;
    };

    const glCodesDescription = (actualCostsItem) => {
        if (actualCostsItem.gl_code) {
            const code = actualCostsItem.gl_code;

            return props.glCodes[code] ? props.glCodes[code].name : null;
        }

        return null;
    };

    const availableAmount = (index = null, list = null) => {
        let amount = 0;
        const items = list ? list : costAllocations;

        items.forEach((item, i) => {
            if (index === null) {
                amount += Number(item.amount);
            } else if (index !== i) {
                amount += Number(item.amount);
            }
        });

        return Math.round((Number(props.actualCosts.amount) - amount) * 100) / 100;
    };

    const dropAllocation = (index) => {
        const data = JSON.parse(JSON.stringify(costAllocations));
        const item = data[index];
        const id = item['id'];

        const sendRequest = !isPlaceholderAllocation(item);

        if (confirm($t('Do you really want to delete this allocation?'))) {
            data.splice(index, 1);
            setCostAllocations(data);
            if (sendRequest) {
                saveAllocations(data);
            }
        }
    };

    const edit = () => {
        if (props.editableGeneralInfo) {
            setEditMode(true);
        }
    };

    const saveAllocations = (dataSet = null, callback = null) => {
        setAllocationsChanged(false);
        const allocations = dataSet === null ? costAllocations : dataSet;

        const oldData = {
            reconciled: props.actualCosts.reconciled,
            type: props.actualCosts.type,
            month: props.actualCosts.month,
            id: props.actualCosts.id,
        };

        axios
            .put(saveUrl, { allocations: allocations })
            .then((res) => {
                props.processActualCostsAfterAllocation(res.data.actualCost, oldData, callback);
                props.fetchFilters();
            })
            .catch((e) => {
                console.log(e);

                // A 422 (or any other failure) used to leave `loading` stuck true —
                // the callback that clears it only ran on success — so the row
                // rendered nothing but a spinner until a page reload, with no
                // indication why. Run the callback here too, and say why it failed.
                const errors = e.response?.data?.errors;
                const messages = errors ? Object.values(errors).flat() : [e.response?.data?.message ?? $t('Something went wrong while saving the allocation.')];
                messages.forEach((message) => props.toast.error(message));

                if (callback) {
                    callback();
                }
            });
    };

    const defaultAllocationData = (items = null) => {
        const amount = availableAmount(null, items);

        const suggestions = props.actualCosts.suggestions;
        const nonWorkforceSuggestions = suggestions.non_workforce;
        const workforceSuggestions = suggestions.workforce;

        if (props.actualCosts.defaultObject) {
            return {
                id: 'new_' + generateKey(),
                type: props.actualCosts.defaultObject.type,
                transaction_type: props.actualCosts.defaultObject.transaction_type,
                typed_valuable_id: props.actualCosts.defaultObject.typed_valuable_id,
                typed_allocatable_id: props.actualCosts.defaultObject.typed_allocatable_id,
                actual_cost_id: props.actualCosts.id,
                amount: amount,
                typed_contractable_id: props.actualCosts.defaultObject.typed_contractable_id,
            };
        }

        let locationId = null;
        if (props.actualCosts.typed_department_id) {
            locationId = props.actualCosts.typed_department_id;
        }

        const res = {
            id: 'new_' + generateKey(),
            type: props.typeNonWorkforce,
            transaction_type: Object.keys(props.transactionTypes)[0] ?? 'cash',
            typed_valuable_id: null,
            typed_allocatable_id: locationId,
            actual_cost_id: props.actualCosts.id,
            amount: amount,
            typed_contractable_id: null,
        };

        if (isArray(nonWorkforceSuggestions) && isArray(workforceSuggestions)) {
            return res;
        }

        const skipWorkforce = isArray(workforceSuggestions) || Object.keys(workforceSuggestions).length > 1;
        const skipNonWorkforce = isArray(nonWorkforceSuggestions) || Object.keys(nonWorkforceSuggestions).length > 1;

        if (skipWorkforce && skipNonWorkforce) {
            return res;
        }

        const processedSuggestions = skipNonWorkforce ? workforceSuggestions : nonWorkforceSuggestions;

        const allocatableId = Object.keys(processedSuggestions)[0] ?? null;
        const valuableId = Object.keys(processedSuggestions[allocatableId].budgetLines)[0] ?? null;
        const contractableId = processedSuggestions[allocatableId].budgetLines[valuableId].contracts[0] ?? null;

        return {
            id: 'new_' + generateKey(),
            type: skipWorkforce ? props.typeNonWorkforce : props.typeWorkforce,
            transaction_type: Object.keys(props.transactionTypes)[0] ?? 'cash',
            typed_valuable_id: valuableId,
            typed_allocatable_id: allocatableId,
            actual_cost_id: props.actualCosts.id,
            amount: amount,
            typed_contractable_id: contractableId,
        };
    };

    const addCostAllocation = () => {
        const newItem = defaultAllocationData();
        const data = JSON.parse(JSON.stringify(costAllocations));
        data.push(newItem);
        setCostAllocations(data);
    };

    /**
     * A commitment is raised against a budget line, so the flag cannot outlive
     * one. Every handler that can blank `typed_valuable_id` runs through here:
     * without it the row keeps rendering as flagged and the next save returns a
     * 422 the reconciler has no way to connect to the change they just made.
     */
    const dropCommitmentFlagWithoutBudgetLine = (allocation) => {
        if (!allocation.typed_valuable_id) {
            allocation.send_for_commitment = false;
        }

        return allocation;
    };

    const changeType = (index, e) => {
        setAllocationsChanged(true);
        const value = e.target.value;
        const data = JSON.parse(JSON.stringify(costAllocations));

        data[index].type = value;

        if (value === 'Workforce' && !!data[index].typed_allocatable_id) {
            const lineItems = directLineItemsList(value, data[index].typed_allocatable_id);
            const dli = lineItems.find((item) => {
                return item.code === 'total-workforce';
            });

            if (dli) {
                data[index].typed_valuable_id = 'position::' + dli.id;
            }
        }

        setCostAllocations(data);
    };

    const changeTransactionType = (index, e) => {
        setAllocationsChanged(true);
        const value = e.target.value;
        const data = JSON.parse(JSON.stringify(costAllocations));

        data[index].transaction_type = value;
        data[index].typed_valuable_id = null;
        data[index].typed_contractable_id = null;
        dropCommitmentFlagWithoutBudgetLine(data[index]);

        setCostAllocations(data);
    };

    const changeTax = (e) => {
        const url = props.updateTaxUrl.replace('ACTUAL_COST_ID', props.actualCosts.id);
        const taxId = e.target.value || null;

        const data = JSON.parse(JSON.stringify(props.actualCosts));
        data.tax_id = taxId;
        props.setActualCostsItem(data);

        axios
            .put(url, { tax_id: taxId })
            .then((res) => {
                props.fetchData();
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                if (!isNull(props.actualCosts.tax_excluded)) {
                    setTaxCanBeEdited(false);
                }
            });
    };

    const changeAllocation = (index, e) => {
        setAllocationsChanged(true);
        const value = e.value;
        const data = JSON.parse(JSON.stringify(costAllocations));

        if (data[index].typed_allocatable_id != value) {
            data[index].typed_valuable_id = null;
        }
        data[index].typed_allocatable_id = value;
        dropCommitmentFlagWithoutBudgetLine(data[index]);
        setCostAllocations(data);
    };

    const changeValuable = (index, e) => {
        setAllocationsChanged(true);
        const value = e.value;
        const data = JSON.parse(JSON.stringify(costAllocations));

        data[index].typed_valuable_id = value;

        const datum = data[index];
        const type = datum.type ?? null;
        const allocatableId = datum.typed_allocatable_id ?? null;

        if (type === 'Workforce' && !!allocatableId && !!value) {
            const lineItems = props.directLineItems[allocatableId][type] ?? [];
            const li = lineItems.find((item) => {
                return item.typed_id === value;
            });

            if (li && li.contracts.length === 1) {
                const contract = li.contracts[0];
                data[index].typed_contractable_id = contract.typed_id;
                data[index].send_for_commitment = false;
            }
        }

        dropCommitmentFlagWithoutBudgetLine(data[index]);

        setCostAllocations(data);
    };

    // The Contract Reference dropdown carries one entry that is not a contract.
    // It must never reach `typed_contractable_id`: getContractableData() takes the
    // id out of any `x::y` string but resolves the type to null on a prefix it
    // does not know, so an unrecognised value persists as an orphan id. Picking a
    // real contract is the other half of the same exclusivity — it clears the flag.
    const changeContractable = (index, e) => {
        setAllocationsChanged(true);
        const value = e.value;
        const data = JSON.parse(JSON.stringify(costAllocations));

        const sendForCommitment = value === SEND_FOR_COMMITMENT_OPTION;

        data[index].send_for_commitment = sendForCommitment;
        data[index].typed_contractable_id = sendForCommitment ? null : value;

        // The only handler that SETS the flag, so it is the only one where the
        // no-budget-line invariant has to be re-checked rather than merely
        // preserved. The option is disabled without one, so this is a backstop
        // against the two ever disagreeing, not the primary guard.
        dropCommitmentFlagWithoutBudgetLine(data[index]);

        setCostAllocations(data);
    };

    const changeAmount = (index, e) => {
        setAllocationsChanged(true);
        let value = e.target.value;
        const max = availableAmount(index);
        value = value > max ? max : value;

        const data = JSON.parse(JSON.stringify(costAllocations));

        data[index].amount = value;
        setCostAllocations(data);
    };

    const processClickAdd = () => {
        setAllocationsChanged(true);
        addCostAllocation();
    };

    const cancelEdit = () => {
        props.fetchData();
        props.setEditedRow(props.actualCosts.id, false);
    };

    const directLineItemsList = (type, typed_allocatable_id) => {
        if (!type || !typed_allocatable_id || !props.directLineItems[typed_allocatable_id]) {
            return [];
        }

        return props.directLineItems[typed_allocatable_id][type];
    };
    const contractLists = (type, typed_allocatable_id, typed_valuable_id) => {
        const sortByReference = (a, b) => a.reference.localeCompare(b.reference);

        const result = {
            recommended: [],
            others: [],
        };

        const trType = props.actualCosts.type;
        const allContracts = props.allContracts[trType][type] ?? [];

        const fillResults = (recommendedContracts) => {
            const recommendedIds = new Set(recommendedContracts.map((c) => c.id));
            const otherContracts = allContracts.filter((c) => !recommendedIds.has(c.id));

            const sortedRecommended = [...recommendedContracts].sort(sortByReference);
            const sortedOthers = [...otherContracts].sort(sortByReference);

            result.recommended = sortedRecommended;
            result.others = sortedOthers;
        };

        if (
            type !== 'Workforce' ||
            !typed_allocatable_id ||
            !typed_valuable_id ||
            (!!typed_valuable_id && typed_valuable_id.startsWith('position::total_workforce'))
        ) {
            const recommendedContracts = props.contracts ?? [];
            fillResults(recommendedContracts);

            return result;
        }

        const budgetLines = props.directLineItems[typed_allocatable_id][type] ?? [];
        const bl = budgetLines.find((item) => {
            return item.typed_id === typed_valuable_id;
        });

        const recommendedContracts = bl ? bl.contracts : (props.contracts ?? []);
        fillResults(recommendedContracts);

        return result;
    };

    const calculateTax = (record) => {
        const value = record.amount;
        const included = !record.tax_excluded;
        const rate = tax ? Number(tax.rate) : 0;
        let val = 0;

        if (included) {
            val = (value * rate) / (100 + rate);
        } else {
            val = (value * rate) / 100;
        }

        return val === 0 ? 0 : val;
    };

    const calculateTotal = (record) => {
        return calculateTaxExcluded(record) + calculateTax(record);
    };

    const calculateTaxExcluded = (record) => {
        if (props.actualCosts.tax_excluded) {
            return +record.amount;
        }

        return +record.amount - calculateTax(record);
    };

    const tooltipContent = () => {
        if (!isNull(props.actualCosts.tax_excluded)) {
            return $t('Tax settings were set based on the linked invoice/bill');
        }

        return $t('The tax could not be determined for this item. Please set manually and split line if necessary.');
    };

    const onDoubleClick = () => {
        setTaxCanBeEdited(true);
    };

    const formatDateTimeForApi = (d) => {
        const pad = (n) => String(n).padStart(2, '0');
        return (
            d.getFullYear() +
            '-' +
            pad(d.getMonth() + 1) +
            '-' +
            pad(d.getDate()) +
            ' ' +
            pad(d.getHours()) +
            ':' +
            pad(d.getMinutes()) +
            ':' +
            pad(d.getSeconds())
        );
    };

    const mergePickedDateWithCurrentTime = (picked) => {
        const current = new Date(props.actualCosts.date);
        const merged = new Date(picked);
        merged.setHours(current.getHours(), current.getMinutes(), current.getSeconds(), 0);
        return merged;
    };

    return (
        <>
            <tr className={props.hidden ? 'tw-hidden' : ''}>
                <td
                    className={`tw-max-w-[20rem] tw-px-2 ${truncateInReadMode}`}
                    rowSpan={calculateRowSpanFromRow()}
                    style={{ zIndex: '2' }}
                    onDoubleClick={edit}
                >
                    {!editMode && (
                        <Tooltip placement="right-end" tooltipContent={props.actualCosts.transaction_description} tooltipType="table">
                            <div className="tw-flex tw-justify-start">
                                <div className="tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis">{props.actualCosts.transaction_description}</div>
                                {!!props.editableGeneralInfo && <ContentListItem info={$t('Double click to edit')} classNames="tw-items-center" />}
                            </div>
                        </Tooltip>
                    )}
                    {editMode && (
                        <div className="tw-flex tw-justify-start tw-items-center">
                            <div className="tw-mr-3 tw-cursor-pointer">
                                <TextButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        props.saveGeneralInfo(
                                            props.actualCosts.id,
                                            () => {
                                                setEditMode(false);
                                            },
                                            () => {
                                                refreshDefaultDate();
                                            },
                                        );
                                    }}
                                    className="tw-flex tw-items-center"
                                >
                                    <CircleCheckIcon className="tw-h-3 tw-w-3 tw-text-green-500" />
                                </TextButton>
                            </div>
                            <div className="tw-mr-3 tw-cursor-pointer">
                                <TextButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditMode(false);
                                        props.resetEdit(defaultGeneralDate);
                                    }}
                                    className="tw-flex tw-items-center"
                                >
                                    <CircleCancelIcon className="tw-h-3 tw-w-3 tw-text-red-500" />
                                </TextButton>
                            </div>

                            <div>
                                <input
                                    type="text"
                                    className={'input text-input !tw-bg-white'}
                                    value={props.actualCosts.transaction_description}
                                    onChange={(e) => props.editActualCostGeneralInfoItem('transaction_description', e.target.value)}
                                    style={{ flexGrow: 1 }}
                                />
                            </div>
                        </div>
                    )}
                </td>
                <td
                    className={`tw-max-w-[20rem] tw-px-2 ${truncateInReadMode}`}
                    rowSpan={calculateRowSpanFromRow()}
                    style={{ zIndex: '2' }}
                    onDoubleClick={edit}
                >
                    {!editMode && (
                        <div className="tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis">
                            {!!props.actualCosts.supplier && (
                                <span className="tw-flex">
                                    <Tooltip placement="right-end" tooltipContent={props.actualCosts.supplier} tooltipType="table">
                                        <span>{props.actualCosts.supplier}</span>
                                    </Tooltip>
                                    <span className="tw-ml-2">
                                        <CircleCheckIcon className="tw-w-4 tw-h-4 tw-text-green" />
                                    </span>
                                </span>
                            )}
                            {!props.actualCosts.supplier && (
                                <Tooltip placement="right-end" tooltipContent={props.actualCosts.supplier_name} tooltipType="table">
                                    <span>{props.actualCosts.supplier_name}</span>
                                </Tooltip>
                            )}
                        </div>
                    )}
                    {editMode && (
                        <div>
                            <select
                                className="select select-input !tw-bg-white !tw-border"
                                value={props.actualCosts.supplier ?? props.actualCosts.supplier_name ?? ''}
                                onChange={(e) => props.editActualCostGeneralInfoItem('supplier', e.target.value)}
                                style={{
                                    paddingLeft: '8px',
                                    paddingBottom: '2px',
                                    paddingTop: '2px',
                                    paddingRight: '30px',
                                    fontSize: '12px',
                                }}
                            >
                                <option value={''}>{$t('No Supplier')}</option>
                                {props.suppliers.map((supplier) => {
                                    return (
                                        <option key={supplier.id} value={supplier.name}>
                                            {supplier.name}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    )}
                </td>
                {props.displayedColumns.documentType && (
                    <td className={truncateInReadMode} rowSpan={calculateRowSpanFromRow()} onDoubleClick={edit}>
                        {!editMode && <span>{props.actualCosts.document_type}</span>}
                        {editMode && (
                            <div>
                                <input
                                    type="text"
                                    className={'input text-input !tw-bg-white'}
                                    value={props.actualCosts.document_type}
                                    onChange={(e) => props.editActualCostGeneralInfoItem('document_type', e.target.value)}
                                    style={{ flexGrow: 1 }}
                                />
                            </div>
                        )}
                    </td>
                )}
                {props.displayedColumns.documentNumber && (
                    <td className={truncateInReadMode} rowSpan={calculateRowSpanFromRow()} onDoubleClick={edit}>
                        {!editMode && <DocumentReferenceCell value={props.actualCosts.document_number} />}
                        {editMode && (
                            <div>
                                <input
                                    type="text"
                                    className={'input text-input !tw-bg-white tw-text-right'}
                                    value={props.actualCosts.document_number}
                                    onChange={(e) => props.editActualCostGeneralInfoItem('document_number', e.target.value)}
                                    style={{ flexGrow: 1 }}
                                />
                            </div>
                        )}
                    </td>
                )}
                {props.displayedColumns.transactionDate && (
                    <td className="tw-text-right" rowSpan={calculateRowSpanFromRow()} onDoubleClick={edit}>
                        {!editMode && <span>{formatDateWithTz(props.actualCosts.date, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY')}</span>}
                        {editMode && (
                            <div className="tw-flex tw-items-center tw-justify-end">
                                <DatePicker
                                    selected={props.actualCosts.date ? new Date(props.actualCosts.date) : null}
                                    onChange={(date) => {
                                        if (!date) {
                                            return;
                                        }
                                        const merged = mergePickedDateWithCurrentTime(date);
                                        props.editActualCostGeneralInfoItem('date', formatDateTimeForApi(merged));
                                    }}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText={$t('Choose date')}
                                    className="input text-input !tw-bg-white tw-text-right"
                                    popperPlacement="bottom-end"
                                    withPortal
                                />
                            </div>
                        )}
                    </td>
                )}
                {props.displayedColumns.glCode && (
                    <td className={`tw-text-left ${truncateInReadMode}`} rowSpan={calculateRowSpanFromRow()} onDoubleClick={edit}>
                        {!editMode && (
                            <span>
                                {props.actualCosts.gl_code} - {glCodesDescription(props.actualCosts)}
                            </span>
                        )}
                        {editMode && (
                            <select
                                className="select select-input !tw-bg-white !tw-border"
                                value={props.actualCosts.gl_code ?? ''}
                                onChange={(e) => props.editActualCostGeneralInfoItem('gl_code', e.target.value)}
                                style={{
                                    paddingLeft: '8px',
                                    paddingBottom: '2px',
                                    paddingTop: '2px',
                                    paddingRight: '30px',
                                    fontSize: '12px',
                                }}
                            >
                                <option value={''}>{$t('No GL code')}</option>
                                {Object.keys(props.glCodes).map((code) => {
                                    const item = props.glCodes[code];

                                    return (
                                        <option key={item.id} value={item.code}>
                                            {item.code} - {item.name}
                                        </option>
                                    );
                                })}
                            </select>
                        )}
                    </td>
                )}
                {!props.taxEnabled && props.displayedColumns.rawAmount && (
                    <td className="inbud-numeric-cell" rowSpan={calculateRowSpanFromRow()} onDoubleClick={edit}>
                        {!editMode && <span>{formatFigure(props.actualCosts.amount)}</span>}
                        {editMode && (
                            <div>
                                <input
                                    type="number"
                                    className={'input text-input !tw-bg-white'}
                                    value={props.actualCosts.amount}
                                    onChange={(e) => props.editActualCostGeneralInfoItem('amount', e.target.value)}
                                    style={{ flexGrow: 1 }}
                                />
                            </div>
                        )}
                    </td>
                )}
                {!!props.taxEnabled && (
                    <>
                        {props.displayedColumns.totalExcludeTax && (
                            <td className="inbud-numeric-cell" rowSpan={calculateRowSpanFromRow()}>
                                <span>{formatFigure(calculateTaxExcluded(props.actualCosts), formatToFixedMoneyWithLocale, { precision: 2 })}</span>
                            </td>
                        )}
                        {props.displayedColumns.tax && (
                            <td className="inbud-numeric-cell" rowSpan={calculateRowSpanFromRow()}>
                                {formatFigure(calculateTax(props.actualCosts), formatToFixedMoneyWithLocale, { precision: 2 })}
                            </td>
                        )}
                        {props.displayedColumns.total && (
                            <td className="inbud-numeric-cell" rowSpan={calculateRowSpanFromRow()}>
                                {formatFigure(calculateTotal(props.actualCosts), formatToFixedMoneyWithLocale, { precision: 2 })}
                            </td>
                        )}
                        {props.displayedColumns.taxExcluded && (
                            <td rowSpan={calculateRowSpanFromRow()} style={{ backgroundColor: isNull(props.actualCosts.tax_excluded) ? 'chocolate' : '' }}>
                                <Tooltip placement="right-end" tooltipContent={tooltipContent()} tooltipType="table">
                                    <div className="tw-flex tw-justify-center tw-h-full tw-items-center tw-min-h-[30px]">
                                        {!isNull(props.actualCosts.tax_excluded) && (
                                            <span>
                                                <CircleCheckIcon className="tw-h-3 tw-w-3 tw-text-green-500" />
                                            </span>
                                        )}
                                    </div>
                                </Tooltip>
                            </td>
                        )}
                        {props.displayedColumns.taxRate && (
                            <td rowSpan={calculateRowSpanFromRow()}>
                                {!taxCanBeEdited && (
                                    <Tooltip placement="bottom" allowTooltip={true} tooltipContent={$t('Double click to edit')} tooltipType="text">
                                        <div onDoubleClick={onDoubleClick}>
                                            {!!tax && (
                                                <span>
                                                    {tax.code}: {tax.display_tax_rate}
                                                </span>
                                            )}
                                            {!tax && <span>-</span>}
                                        </div>
                                    </Tooltip>
                                )}
                                {taxCanBeEdited && (
                                    <>
                                        <div className="tw-flex tw-items-center">
                                            <select
                                                className="select select-input !tw-bg-white !tw-border"
                                                value={props.actualCosts.tax_id || ''}
                                                onChange={(e) => changeTax(e)}
                                                style={{
                                                    paddingLeft: '8px',
                                                    paddingBottom: '2px',
                                                    paddingTop: '2px',
                                                    paddingRight: '30px',
                                                    fontSize: '12px',
                                                }}
                                            >
                                                {!props.actualCosts.tax_id && <option value={''}>{$t('Choose Tax Rate')}</option>}
                                                {props.taxes.map((item) => {
                                                    return (
                                                        <option key={item.id} value={item.id}>
                                                            {item.code}: {item.display_tax_rate}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            <div className="tw-ml-2">
                                                {!isNull(props.actualCosts.tax_excluded) && (
                                                    <TextButton
                                                        onClick={(e) => {
                                                            setTaxCanBeEdited(false);
                                                        }}
                                                        className="tw-flex tw-items-center"
                                                    >
                                                        <CircleCancelIcon className="tw-h-3 tw-w-3 tw-text-red-500" />
                                                    </TextButton>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </td>
                        )}
                    </>
                )}

                {costAllocations.length === 0 && (
                    <td className="tw-cursor-pointer" colSpan={7} onClick={processClickAdd}>
                        <div className="tw-flex tw-justify-center tw-items-center">
                            <div className={'tw-h-9 tw-w-9 tw-bg-blue tw-flex tw-justify-center tw-items-center tw-rounded-full'}>
                                <PlusCircleIcon className={'tw-h-3.5 tw-w-3.5 tw-text-white'} />
                            </div>
                        </div>
                    </td>
                )}
                {costAllocations.length >= 1 && (
                    <Fragment>
                        {costAllocations.map((row, index) => {
                            if (index === 0) {
                                return (
                                    <ActualCostsAllocationSubRow
                                        departments={props.departments[type]}
                                        directLineItems={directLineItemsList(row.type, row.typed_allocatable_id)}
                                        contracts={contractLists(row.type, row.typed_allocatable_id, row.typed_valuable_id)}
                                        key={`${props.actualCosts.id}_${index}`}
                                        data={row}
                                        index={index}
                                        changeAllocation={changeAllocation}
                                        changeValuable={changeValuable}
                                        changeContractable={changeContractable}
                                        changeType={changeType}
                                        changeTransactionType={changeTransactionType}
                                        changeTax={changeTax}
                                        changeAmount={changeAmount}
                                        availableAmount={availableAmount()}
                                        addCostAllocation={addCostAllocation}
                                        processClickAdd={processClickAdd}
                                        setEditedRow={() => props.setEditedRow(props.actualCosts.id)}
                                        resetEditedRow={() => props.setEditedRow(props.actualCosts.id, false)}
                                        processClickDelete={dropAllocation}
                                        editedRow={props.editedRow}
                                        manuallyOpenedRow={props.manuallyOpenedRow}
                                        actualCosts={props.actualCosts}
                                        cancelEdit={cancelEdit}
                                        saveAllocations={saveAllocations}
                                        types={props.types}
                                        transactionTypes={props.transactionTypes}
                                        typeWorkforce={props.typeWorkforce}
                                        typeNonWorkforce={props.typeNonWorkforce}
                                        taxes={props.taxes}
                                        taxEnabled={props.taxEnabled}
                                        suggestions={suggestions}
                                        displayedColumns={props.displayedColumns}
                                        setAllocationsChanged={setAllocationsChanged}
                                        checkActualCostsStatus={checkTransactionStatusUrl ? checkActualCostsStatus : null}
                                        refresh={refreshUrl ? refreshTransaction : null}
                                        archiveActualCost={props.archiveActualCost}
                                        isSelectable={props.isSelectable}
                                        selected={props.selected}
                                        isEligible={props.isEligible}
                                        toggleSelection={props.toggleSelection}
                                        bulkApply={props.bulkApply}
                                    />
                                );
                            }
                        })}
                    </Fragment>
                )}
            </tr>
            {costAllocations.length > 1 && (
                <Fragment>
                    {costAllocations.map((row, index) => {
                        if (index > 0) {
                            return (
                                <tr key={`${props.actualCosts.id}_${index}`} className={props.hidden ? 'tw-hidden' : ''}>
                                    <ActualCostsAllocationSubRow
                                        departments={props.departments[type]}
                                        directLineItems={directLineItemsList(row.type, row.typed_allocatable_id)}
                                        contracts={contractLists(row.type, row.typed_allocatable_id, row.typed_valuable_id)}
                                        data={row}
                                        index={index}
                                        changeAllocation={changeAllocation}
                                        changeValuable={changeValuable}
                                        changeContractable={changeContractable}
                                        changeType={changeType}
                                        changeTransactionType={changeTransactionType}
                                        changeTax={changeTax}
                                        changeAmount={changeAmount}
                                        availableAmount={availableAmount()}
                                        addCostAllocation={addCostAllocation}
                                        processClickAdd={processClickAdd}
                                        setEditedRow={() => props.setEditedRow(props.actualCosts.id)}
                                        resetEditedRow={() => props.setEditedRow(props.actualCosts.id, false)}
                                        processClickDelete={dropAllocation}
                                        editedRow={props.editedRow}
                                        manuallyOpenedRow={props.manuallyOpenedRow}
                                        actualCosts={props.actualCosts}
                                        cancelEdit={cancelEdit}
                                        saveAllocations={saveAllocations}
                                        types={props.types}
                                        transactionTypes={props.transactionTypes}
                                        typeWorkforce={props.typeWorkforce}
                                        typeNonWorkforce={props.typeNonWorkforce}
                                        taxes={props.taxes}
                                        taxEnabled={props.taxEnabled}
                                        suggestions={suggestions}
                                        displayedColumns={props.displayedColumns}
                                        setAllocationsChanged={setAllocationsChanged}
                                        checkActualCostsStatus={checkTransactionStatusUrl ? checkActualCostsStatus : null}
                                        bulkApply={props.bulkApply}
                                    />
                                </tr>
                            );
                        }
                    })}
                </Fragment>
            )}
        </>
    );
});

export default ActualCostsAllocationRow;
